import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import {
    ITCIMarketplaceListing,
    ITCIMarketplaceListingMetaData,
    ITCIMarketplaceV2MenuInput,
    ITCIMarketplaceV2MenuOutput,
    ITCIUserInfo,
    TCI_MARKETPLACE_LISTING_EDITOR_MODE,
    TCI_MARKETPLACE_NOTIFICATION_TYPE,
    TCI_MARKETPLACE_ORG_TYPE,
    TCIMainMenu,
    TCIMarketplaceListingStatus,
    TCIMarketplaceV2DetailsMenu,
    TCITenantSelectorType
} from "@tci/common";
import {
    BaseAppDetailMenuComponentInput,
    BaseAppDetailMenuComponentOutput
} from "../../ng-app-details/ng-app-details-menu";
import { NgMarketplaceV2Service } from "../../ng-marketplace-v2/ng-marketplace-v2.service";
import { NgToastrService } from "../../ng-shared/ng-components/ng-toastr/ng-toastr.service";
import { AbstractTCITenantComponent } from "../../ng-tenant/tci-tenant-component";
import { ITCIAppDetailsMenuOutput } from "../../ng-tenant/tci-tenant-components";
import { BaseMarketplaceCreateListingService } from "./base-marketplace-create-listing.service";
import { Observable, Subscription } from "rxjs";

const MK_ROUTE = TCIMainMenu.MAIN_MENU_MARKETPLACE_V2.getRouteId(); // "marketplace"

/**
 * BaseMarketplaceCreateListing
 */
@Component({
    selector: TCITenantSelectorType.TCI_APP_DETAIL_CREATE_LISTING.toString(),
    templateUrl: "./base-marketplace-create-listing.html",
    styleUrls: ["./base-marketplace-create-listing.less"]
})
// tslint:disable-next-line:component-class-suffix
export class BaseMarketplaceCreateListing<E, I extends BaseAppDetailMenuComponentInput,
    O extends BaseAppDetailMenuComponentOutput<ITCIAppDetailsMenuOutput>>
    extends AbstractTCITenantComponent<E, I, O>
    implements OnInit, OnDestroy {

    inputData: ITCIMarketplaceV2MenuInput;
    createListing = true;
    shouldUpgrade = false;
    shouldUpdate = false;
    isPendingApproval = false;
    isPrivate = false;

    ready = false;
    mountEditor = false;
    showEditor = false;
    showHMMenu = false;
    showBeforeUpgrade = false;
    showBeforeUpdate = false;
    showAfterCreate = false;
    showAfterUpdate = false;
    busy = false;

    modalDimensions: { width: number, height: number };

    // User info
    userInfo: ITCIUserInfo;

    private getEditorModeObs: Observable<TCI_MARKETPLACE_LISTING_EDITOR_MODE[]>;
    private subscriptions: Subscription[] = [];

    constructor(
        protected mkCreateListingService: BaseMarketplaceCreateListingService,
        protected mkService: NgMarketplaceV2Service,
        protected translate: TranslateService,
        private router: Router,
        protected toastr: NgToastrService
    ) {
        super();

        // Get modal dimensions from menu options
        this.modalDimensions = {
            width: TCIMarketplaceV2DetailsMenu.TCI_MENU_MKP_EDIT_DETAILS.getModalType().getWidth(),
            height: TCIMarketplaceV2DetailsMenu.TCI_MENU_MKP_EDIT_DETAILS.getModalType().getHeight()
        };

        this.subscriptions.push(
            this.mkService.getNotificationSubject()
                .filter(change =>
                    change.listing && (
                        (this.inputData && this.inputData.listing && this.inputData.listing.id === change.listing.id) ||
                        (this.input && this.input.application && this.input.application.id === change.listing.creatorAssetId)
                    )
                )
                .subscribe(change => {
                    console.log("Marketplace listing metadata change detected", change);

                    switch (change.type) {
                        case TCI_MARKETPLACE_NOTIFICATION_TYPE.Create:
                            this.router.navigate([MK_ROUTE, change.listing.id]);
                            return;

                        case TCI_MARKETPLACE_NOTIFICATION_TYPE.Update:
                        case TCI_MARKETPLACE_NOTIFICATION_TYPE.Refresh:
                            this.busy = false;
                            this.mountEditor = false;
                            this.showBeforeUpgrade = false;
                            this.refresh(change.listing as ITCIMarketplaceListingMetaData);
                            return;
                    }
                }),

            mkCreateListingService.getUserInfo().subscribe(
                userInfo => this.userInfo = userInfo
            )
        );
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.getInitMarketplaceListingData(this.input)
                .subscribe(() => this.ready = true)
        );
    }

    ngOnDestroy() {
        this.subscriptions
            .filter(s => !s.closed)
            .forEach(s => s.unsubscribe());
    }

    /**
     * refresh
     * @param refreshData
     */
    refresh(refreshData: ITCIMarketplaceListingMetaData): void {
        this.subscriptions.push(this.getInitMarketplaceListingData(this.input).subscribe());
        console.log("The refresh data from tenant: ", refreshData);
    }

    /**
     * Tenant needs to override this function to provide
     * @param dataInput
     * @protected
     */
    protected getListingMetaData(dataInput: I): Observable<ITCIMarketplaceListingMetaData> {
        console.error("Marketplace base create listing 'getListingMetaData' should not be called but overriden by tenant");
        return Observable.of({ creatorAssetId: "" } as any);
    }

    /**
     * returns the editor mode based on TCI checks to see if a listing exists for Upgrade
     * Or not exists for create. Tenants dont need to override this function
     * @param dataInput
     * @protected
     */
    protected getEditorMode(dataInput: I, metaData: ITCIMarketplaceListingMetaData): Observable<TCI_MARKETPLACE_LISTING_EDITOR_MODE[]> {
        if (!this.getEditorModeObs) {
            const createObs = this.mkService.canCreateComponentFunction(dataInput.componentOption, this.input, metaData, false);
            const upgradeObs = this.mkService.canUpdateComponentFunction(dataInput.componentOption, this.input, metaData);
            const updateObs = this.mkService.canUpgradeComponentFunction(dataInput.componentOption, this.input, metaData);
            const pendingApprovalObs = this.mkService.isListingStatusForOrgType(
                metaData,
                TCI_MARKETPLACE_ORG_TYPE.Provider,
                TCIMarketplaceListingStatus.PENDING_APPROVAL,
                true);
            const privateObs = this.mkService.isListingStatusForOrgType(
                metaData,
                TCI_MARKETPLACE_ORG_TYPE.Provider,
                TCIMarketplaceListingStatus.PRIVATELY_AVAILABLE,
                true);

            this.getEditorModeObs = Observable.combineLatest(
                createObs, upgradeObs, updateObs, pendingApprovalObs, privateObs,
                (create, update, upgrade, pendingApproval, isPrivate) => ({ create, update, upgrade, pendingApproval, isPrivate })
            ).map(r => {
                const modes = [] as TCI_MARKETPLACE_LISTING_EDITOR_MODE[];
                console.log("Editor mode result", r);
                if (r.create) {
                    modes.push(TCI_MARKETPLACE_LISTING_EDITOR_MODE.CREATE);
                }
                if (r.update) {
                    modes.push(TCI_MARKETPLACE_LISTING_EDITOR_MODE.EDIT);
                }
                if (r.upgrade) {
                    modes.push(TCI_MARKETPLACE_LISTING_EDITOR_MODE.UPGRADE);
                }
                if (r.pendingApproval) {
                    modes.push(TCI_MARKETPLACE_LISTING_EDITOR_MODE.PENDING_APPROVAL);
                }

                this.isPrivate = r.isPrivate;
                return modes;
            }).do(() => delete this.getEditorModeObs).share();
        }

        return this.getEditorModeObs;
    }

    /**
     * Tenants do not need to override this method unless they are also detecting the create | upgrade mode
     * @protected
     */
    protected getInitMarketplaceListingData(dataInput: I): Observable<null> {
        /**
         * The tenant always gives the current metadata irrespective of editor modes. TCI is responsible for
         * detecting update|create|upgrade and it is done based on the metadata asset id and asset version
         */
        return this.getListingMetaData(dataInput)
            .flatMap(listing => {
                if (listing) {
                    return this.getEditorMode(dataInput, listing)
                        .map(editorMode => {
                            this.inputData = { editorMode: null, listing }; // editor mode is fixed from the menu
                            this.createListing = editorMode.includes(TCI_MARKETPLACE_LISTING_EDITOR_MODE.CREATE);
                            this.shouldUpdate = editorMode.includes(TCI_MARKETPLACE_LISTING_EDITOR_MODE.EDIT);
                            this.shouldUpgrade = editorMode.includes(TCI_MARKETPLACE_LISTING_EDITOR_MODE.UPGRADE);
                            this.isPendingApproval = editorMode.includes(TCI_MARKETPLACE_LISTING_EDITOR_MODE.PENDING_APPROVAL);
                            console.log("The data will pass into create MK dialog: ", this.inputData);
                            return null;
                        });
                }
            });
    }

    checkBeforeCreateDialog() {
        // Switch between Create or Popup
        if (this.createListing) {
            this.inputData.editorMode = TCI_MARKETPLACE_LISTING_EDITOR_MODE.CREATE;
            this.openEditorDialog();
        } else {
            this.openHMMenu();
        }
    }

    checkBeforeOpenEditDialog() {
        this.inputData.editorMode = TCI_MARKETPLACE_LISTING_EDITOR_MODE.EDIT;
        this.mkService.isListingPublic(this.inputData.listing)
            .subscribe(isPublic => {
                if (isPublic) {
                    this.showBeforeUpdate = true;
                } else {
                    this.openEditorDialog();
                }
            });
        this.closeHMMenu();
    }

    closeEditorDialog($event: ITCIMarketplaceV2MenuOutput<ITCIMarketplaceListing>): void {
        this.showEditor = false;
        this.mountEditor = false;
        this.closeModal($event, false);
        /*
        // Do not support for display after create/edit modal, use toastr message instead of.
        if ($event.submitActionType === TCI_MARKETPLACE_LISTING_EDITOR_SUBMIT_ACTION.SUBMIT_PRIVATE_NEW) {
            this.showAfterCreate = true;
        }
        if ($event.submitActionType === TCI_MARKETPLACE_LISTING_EDITOR_SUBMIT_ACTION.SUBMIT_PRIVATE_UPDATE) {
            this.showAfterUpdate = true;
        }
        */
    }

    closeEditorModal() {
        this.showBeforeUpdate = false;
    }

    openEditorDialog() {
        this.closeEditorModal();
        this.showEditor = true;
        this.mountEditor = true;
        this.closeHMMenu();
    }

    private openHMMenu() {
        this.showHMMenu = true;
    }

    closeHMMenu() {
        this.showHMMenu = false;
    }

    checkBeforeOpenUpgradeDialog() {
        this.inputData.editorMode = TCI_MARKETPLACE_LISTING_EDITOR_MODE.UPGRADE;
        this.showBeforeUpgrade = true;
        this.closeHMMenu();
    }

    // Note: the upgrade dialog UI visibility is hidden, user just can not see it in the browser, but it is there in HTML dom.
    closeUpgradeModal() {
        this.showBeforeUpgrade = false;
    }

    // Note: the upgrade dialog UI visibility is hidden, user just can not see it in the browser, but it is there in HTML dom.
    // On upgrade agreement the editor component will get mounted
    // and execute the upgrade automatically on ngOnInit
    openUpgradeDialog() {
        this.busy = true;
        this.inputData.editorMode = TCI_MARKETPLACE_LISTING_EDITOR_MODE.UPGRADE;
        this.mountEditor = true;
    }

    closeAfterCreateModal() {
        this.showAfterCreate = false;
    }

    closeAfterUpdateModal() {
        this.showAfterUpdate = false;
    }

    /**
     * This function is called when the Create/Update/Upgrade dialog closes
     * The tenant needs to check $event to know if there was a cancel/submit
     * and if any errors. The output data is in $event.result
     * This function also emits the $event to its parent hosting component
     * using @Output() output
     * @param $event
     * @param refresh
     * @protected
     */
    protected closeModal($event: ITCIMarketplaceV2MenuOutput<ITCIMarketplaceListing>,
        refresh: boolean): Observable<ITCIAppDetailsMenuOutput> {
        const e: ITCIAppDetailsMenuOutput = {
            menuItemSelector: this.input.componentSelector,
            closeModal: $event.closeModal,
            refresh: refresh && $event.refresh,
            result: $event as ITCIMarketplaceV2MenuOutput<ITCIMarketplaceListing>
        };
        this.output.emit(e);
        return Observable.of(e);
    }

    agreedGuardrail() {
        if (this.showBeforeUpdate) {
            this.openEditorDialog();
        }

        if (this.showBeforeUpgrade) {
            this.openUpgradeDialog();
        }
    }

    closeGuardrail() {
        this.closeUpgradeModal();
        this.closeEditorModal();
    }
}
